<template>
  <div>
    <BaseModal :name="name" width-class="tw-max-w-[550px]">
      <div>
        <div
          class="tw-flex tw-justify-center tw-rounded-t-xl tw-bg-error-500 tw-p-3"
        >
          <WarningIcon class="tw-h-6 tw-w-6 tw-text-white" />
        </div>
        <div class="tw-p-5 tw-text-center">
          <h2 class="tw-heading-2">Free sessions limit reached</h2>

          <p v-if="!hideDescription" class="tw-mt-3 tw-text-neutral-500">
            Sorry, you have already reached the limit of
            <strong class="tw-font-medium">
              {{ accessControl.subscription.privileges.freeCalls.count }}
              sessions
            </strong>
            in the queue at one time.
            <a
              class="tw-link"
              href="https://www.growthmentor.com/faq/active-session-limit/"
              target="_blank"
            >
              Click here to learn more.
            </a>
          </p>

          <div
            v-if="accessControl.subscription.privileges.doubleUpQueue"
            class="tw-mt-4 tw-rounded-md tw-border tw-border-success-100 tw-bg-gradient-to-r tw-from-success-50 tw-via-white tw-to-white tw-p-6"
          >
            <div
              class="tw-text-xs tw-font-semibold tw-uppercase tw-text-success-500"
            >
              Upgrade free sessions limit
            </div>
            <div class="tw-mt-3 tw-text-lg">Book 6 Free Calls at a Time</div>
            <p class="tw-text-sm tw-text-neutral-500">
              One-time fee for 30 days of double queue
            </p>
            <p
              class="tw-inline-flex tw-items-center tw-text-sm tw-text-neutral-500"
            >
              Pay today:
              <SkeletonLoader
                :is-loading="total === 0"
                size-classes="tw-w-8 tw-h-4 tw-mx-1 tw-rounded"
              >
                <span class="tw-mx-1">
                  {{ formatCurrency(total) }}
                </span>
              </SkeletonLoader>
              <template v-if="tax > 0">
                incl. VAT
                <PricingDetailsTooltip
                  :tax="tax"
                  :subtotal="subtotal"
                  class="tw-ml-1"
                />
              </template>
            </p>
            <div class="tw-mt-3">
              <transition name="fade" mode="out-in">
                <div
                  v-if="areConfirmationButtonsShown"
                  class="tw-flex tw-items-center tw-justify-center tw-space-x-4"
                >
                  <BaseButton
                    color="outline-gray"
                    @click="hideConfirmationButtons"
                  >
                    Cancel payment
                  </BaseButton>
                  <BaseButton color="outline-green" @click="doubleUpQueue">
                    Confirm payment
                  </BaseButton>
                </div>
                <BaseButton
                  v-else
                  @click="showConfirmationButtons"
                  color="outline-green"
                  class="tw-bg-transparent tw-uppercase"
                  :is-disabled="doubleUpQueueForm.processing"
                >
                  <LoadingCircle
                    v-if="doubleUpQueueForm.processing"
                    size="16"
                    class="tw-mr-2"
                  />
                  <CopyLinkIcon v-else class="tw-mr-2 tw-w-4" />
                  Double up your queue
                </BaseButton>
              </transition>
            </div>
            <FormErrors
              :errors="doubleUpQueueForm.errors.creditCard"
              class="tw-mt-4"
            />
          </div>

          <div
            v-if="isSubscriptionTier(SUBSCRIPTION_TIER.BABY)"
            class="tw-mt-4 tw-rounded-md tw-border tw-border-success-100 tw-bg-gradient-to-r tw-from-success-50 tw-via-white tw-to-white tw-p-6"
          >
            <div
              class="tw-text-xs tw-font-semibold tw-uppercase tw-text-success-500"
            >
              Membership upgrade
            </div>
            <div class="tw-mt-3 tw-text-lg">Upgrade your Membership</div>
            <p class="tw-text-sm tw-text-neutral-500">
              You may book paid calls or upgrade your plan to get 3 free calls
              in the queue at once.
            </p>
            <BaseButton
              color="outline-green"
              class="tw-mt-3 tw-uppercase"
              href="/subscription/plans"
              is-inertia-link
            >
              Upgrade your plan
              <ExternalLinkIcon class="tw-ml-2 tw-h-4 tw-w-4" />
            </BaseButton>
          </div>

          <BaseButton
            color="outline-gray"
            @click="closeModal(name)"
            class="tw-mt-6"
            :is-disabled="doubleUpQueueForm.processing"
          >
            Close
          </BaseButton>
        </div>
      </div>
    </BaseModal>

    <ConfirmationModal
      :name="confirmationModalName"
      header="Payment confirmation"
      description="You will be charged $100 + tax and you will be able to book 6 free calls at a time for 30 days."
      cancel-button-text="Go back"
      confirmation-button-text="Confirm"
    >
      <template #icon>
        <WarningIcon />
      </template>
    </ConfirmationModal>
    <BaseModal :name="successModalName" width-class="tw-max-w-[550px]">
      <div>
        <div
          class="tw-flex tw-justify-center tw-rounded-t-xl tw-bg-success-500 tw-p-3 tw-text-white"
        >
          <CheckOutlineIcon class="tw-h-6 tw-w-6" />
        </div>
        <div class="tw-p-5 tw-text-center">
          <h2 class="tw-heading-2">Payment successful</h2>
          <p class="tw-text-neutral-400">
            Now you can book up to 6 free calls at a time for 30 days.
          </p>
          <div class="tw-mt-4 tw-text-right">
            <BaseButton
              color="outline-gray"
              @click="closeModal(successModalName)"
            >
              Close
            </BaseButton>
          </div>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script setup>
import BaseButton from '../BaseButton.vue'
import WarningIcon from '@/assets/images/icons/warning.svg'
import CopyLinkIcon from '@/assets/images/colored-icons/copy-link.svg'
import LoadingCircle from '@/shared/components/LoadingCircle.vue'
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal.vue'
import CheckOutlineIcon from '@/assets/images/icons/check-outline-2.svg'
import ExternalLinkIcon from '@/assets/images/icons/external-link.svg'
import FormErrors from '@/shared/components/forms/FormErrors.vue'
import { useForm } from '@inertiajs/vue3'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useModals } from '@/shared/composables/useModals.js'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { SUBSCRIPTION_TIER } from '@/shared/data/permissionSystem.js'
import BaseModal from '@/shared/components/BaseModal.vue'
import { useDoubleUpQueueTax } from '@/shared/composables/taxes/useDoubleUpQueueTax.js'
import SkeletonLoader from '@/shared/components/SkeletonLoader.vue'
import { formatCurrency } from '../../utils/formatCurrency.js'
import PricingDetailsTooltip from '@/shared/components/PricingDetailsTooltip.vue'

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  hideDescription: {
    type: Boolean,
    default: false,
  },
})

const { accessControl, isSubscriptionTier } = usePermissionSystem()
const { closeModal, openModal } = useModals()
const { tax, fetchDoubleUpQueueTax, subtotal, total } = useDoubleUpQueueTax()

const doubleUpQueueForm = useForm({})
const areConfirmationButtonsShown = ref(false)

const confirmationModalName = computed(() => {
  return props.name + '-confirmation'
})
const successModalName = computed(() => {
  return props.name + '-success'
})

const showConfirmationButtons = () => {
  areConfirmationButtonsShown.value = true
}

const hideConfirmationButtons = () => {
  areConfirmationButtonsShown.value = false
}

const doubleUpQueue = () => {
  hideConfirmationButtons()
  doubleUpQueueForm.patch(`/mentee/session_limits`, {
    preserveScroll: true,
    onSuccess: () => {
      closeModal(props.name)
      nextTick(() => {
        openModal(successModalName.value)
      })
    },
  })
}

onMounted(() => {
  if (!accessControl.value.subscription.privileges.doubleUpQueue) return
  fetchDoubleUpQueueTax()
})
</script>
